// Reusable square-aspect crop modal that mirrors the UX used by PhotoUpload.js
// (the public team-link upload). Powered by react-image-crop, which is already
// a dependency.

import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const PhotoCropModal = ({
    show,
    imageUrl,
    onConfirm,
    onCancel,
    title,
    subtitle,
    onClose,        // optional: renders an X in the header to dismiss without cropping
    onUseOriginal,  // optional: renders an "Upload as is" button (skips cropping)
    cancelText,     // optional: label for the cancel button (default "Retake")
    confirmText,    // optional: label for the apply button (default "Apply Crop")
    freeCrop,       // optional: when true, unlock the 1:1 aspect so all 8 handles (edges + corners) are draggable (output is NOT square)
}) => {
    const imageRef = useRef(null);
    const previewCanvasRef = useRef(null);

    const [crop, setCrop] = useState({
        unit: 'px',
        width: 200,
        height: 200,
        x: 0,
        y: 0,
        aspect: 1,
    });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [processing, setProcessing] = useState(false);

    // Center the initial 80% square crop once the image loads.
    const handleImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const size = Math.min(width, height) * 0.8;
        const x = (width - size) / 2;
        const y = (height - size) / 2;
        const initialCrop = {
            unit: 'px',
            width: size,
            height: size,
            x,
            y,
            aspect: 1,
        };
        setCrop(initialCrop);
        // Set completedCrop too so Apply Crop works even if the user doesn't drag.
        setCompletedCrop(initialCrop);
    };

    const handleApply = useCallback(() => {
        if (!completedCrop || !imageRef.current || !previewCanvasRef.current) return;

        const image = imageRef.current;
        const canvas = previewCanvasRef.current;
        const c = completedCrop;

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        const ctx = canvas.getContext('2d');
        const pixelRatio = window.devicePixelRatio || 1;

        canvas.width = c.width * pixelRatio * scaleX;
        canvas.height = c.height * pixelRatio * scaleY;

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
            image,
            c.x * scaleX,
            c.y * scaleY,
            c.width * scaleX,
            c.height * scaleY,
            0,
            0,
            c.width * scaleX,
            c.height * scaleY
        );

        setProcessing(true);
        canvas.toBlob(
            (blob) => {
                setProcessing(false);
                if (blob) onConfirm(blob);
            },
            'image/jpeg',
            0.95
        );
    }, [completedCrop, onConfirm]);

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-[70] flex items-center justify-center p-4">
            {/* Enlarge the react-image-crop corner handles so they're easy to grab.
                With a locked square aspect, the library shows only the 4 corners
                (edge handles are hidden because dragging an edge can't stay square). */}
            <style>{`
                .ppu-crop .ReactCrop__drag-handle {
                    width: 18px;
                    height: 18px;
                    background-color: #4f46e5;
                    border: 2px solid #fff;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.5);
                }
            `}</style>
            <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full p-6">
                <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">{title || 'Crop Your Photo'}</h3>
                        <p className="text-sm text-gray-500">{subtitle || 'Adjust the square to frame the face properly'}</p>
                    </div>
                    {onClose && (
                        <button
                            onClick={onClose}
                            disabled={processing}
                            aria-label="Close"
                            className="shrink-0 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                <div className="ppu-crop relative bg-gray-100 rounded-lg overflow-hidden mb-4 flex items-center justify-center">
                    <ReactCrop
                        crop={crop}
                        onChange={(c) => setCrop(c)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={freeCrop ? undefined : 1}
                        className="max-h-[400px]"
                    >
                        <img
                            ref={imageRef}
                            src={imageUrl}
                            alt="Crop preview"
                            style={{ maxHeight: '400px', width: 'auto' }}
                            onLoad={handleImageLoad}
                        />
                    </ReactCrop>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={onCancel}
                        disabled={processing}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        {cancelText || 'Retake'}
                    </button>
                    {onUseOriginal && (
                        <button
                            onClick={onUseOriginal}
                            disabled={processing}
                            className="flex-1 px-4 py-2 border border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50"
                        >
                            Upload as is
                        </button>
                    )}
                    <button
                        onClick={handleApply}
                        disabled={!completedCrop || processing}
                        className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    >
                        {processing ? 'Processing...' : (confirmText || 'Apply Crop')}
                    </button>
                </div>

                <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
            </div>
        </div>
    );
};

export default PhotoCropModal;
